/**
 * What an upload into the file tree will do, before anything is sent: which files go where, which are left out
 * (junk the OS leaves around, `node_modules`/`.git` unless asked for), which already exist, and whether it fits the
 * server's limits. Pure functions, so the dialog and the tests share them.
 */

/**
 * vfs-service's POST /upload as an older server took it: the archive (200 MB) and what it unpacks to (files, bytes).
 * Only a fallback: the real numbers come from the server (serverLimits.js, GET /upload/limits) and are passed in.
 */
export const SERVER_LIMITS = {
  maxArchiveBytes: 200 * 1024 * 1024,
  maxFiles: 10000,
  maxExtractedBytes: 512 * 1024 * 1024,
};
/** Above this the dialog asks the person to double-check before sending. */
export const LARGE_UPLOAD_BYTES = 50 * 1024 * 1024;

const JUNK_NAMES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);
const JUNK_FOLDERS = new Set(['__MACOSX']);
/** Folders skipped unless the person turns "Skip node_modules and .git" off. */
export const HEAVY_FOLDERS = new Set(['node_modules', '.git']);
/** Folders the server never writes (a service's history lives in `.git`): left out whatever the toggle says. */
export const RESERVED_FOLDERS = new Set(['.git', '.vfs-meta']);

export const CONFLICT_POLICIES = [
  { value: 'overwrite', label: 'Overwrite all' },
  { value: 'skip', label: 'Skip existing' },
  { value: 'rename', label: 'Keep both (rename)' },
];

const segmentsOf = (relPath) => String(relPath || '').replace(/\\/g, '/').split('/').filter((segment) => segment && segment !== '.');

/** `a/b`, `a//b/`, `./a` → `a/b` / `a`; '' for the root. */
export function cleanPath(relPath) {
  return segmentsOf(relPath).join('/');
}

export function joinPath(...parts) {
  return parts.map(cleanPath).filter(Boolean).join('/');
}

/** Why a path is left out ('junk' | 'skipped-folder' | 'reserved' | 'unsafe'), or null when it is uploaded. */
export function exclusionReason(relPath, { skipHeavy = true } = {}) {
  const segments = segmentsOf(relPath);
  if (segments.length === 0 || segments.includes('..') || String(relPath).startsWith('/')) { return 'unsafe'; }
  if (JUNK_NAMES.has(segments[segments.length - 1]) || segments.some((segment) => JUNK_FOLDERS.has(segment))) { return 'junk'; }
  // `.git` is left out even with the toggle off: it would clash with the service's own history.
  if (segments.some((segment) => RESERVED_FOLDERS.has(segment))) { return skipHeavy ? 'skipped-folder' : 'reserved'; }
  if (skipHeavy && segments.some((segment) => HEAVY_FOLDERS.has(segment))) { return 'skipped-folder'; }
  return null;
}

/**
 * entries: [{ relPath, size, file }] as collected (relPath relative to what was picked or dropped, folders kept).
 * existing: Set of destination paths (target-relative to the tree) that already exist.
 * Returns { files: [{relPath, destPath, size, file, exists}], excluded: [{relPath, reason}], folderCount, totalBytes,
 * conflicts: [destPath], problems: [message], warnings: [message] }.
 */
export function buildPlan(entries, { targetFolder = '', skipHeavy = true, existing = new Set(), emptyFolders = [], limits = SERVER_LIMITS } = {}) {
  const files = [];
  const excluded = [];
  const folders = new Set();
  for (const entry of entries) {
    const relPath = cleanPath(entry.relPath);
    const reason = exclusionReason(entry.relPath, { skipHeavy });
    if (reason) {
      excluded.push({ relPath: relPath || String(entry.relPath), reason });
      continue;
    }
    const segments = relPath.split('/');
    for (let index = 1; index < segments.length; index += 1) { folders.add(segments.slice(0, index).join('/')); }
    const destPath = joinPath(targetFolder, relPath);
    files.push({ relPath, destPath, size: entry.size || 0, file: entry.file, exists: existing.has(destPath) });
  }
  for (const folder of emptyFolders) {
    const relPath = cleanPath(folder);
    if (relPath && !exclusionReason(`${relPath}/x`, { skipHeavy })) {
      const segments = relPath.split('/');
      for (let index = 1; index <= segments.length; index += 1) { folders.add(segments.slice(0, index).join('/')); }
    }
  }
  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const problems = [];
  const warnings = [];
  if (files.length > limits.maxFiles) {
    problems.push(`${files.length} files is more than the ${limits.maxFiles} one upload may hold. Upload the folder in parts.`);
  }
  if (totalBytes > limits.maxExtractedBytes) {
    problems.push(`${formatBytes(totalBytes)} is more than the ${formatBytes(limits.maxExtractedBytes)} one upload may hold. Upload it in parts.`);
  }
  const tooBig = files.filter((file) => file.size > limits.maxArchiveBytes);
  if (tooBig.length) {
    problems.push(`${tooBig.map((file) => file.relPath).slice(0, 3).join(', ')} ${tooBig.length === 1 ? 'is' : 'are'} larger than ${formatBytes(limits.maxArchiveBytes)}, the most one upload may be.`);
  }
  // The sandbox's free disk, when the server told it: an upload that cannot fit is stopped here, not halfway.
  if (typeof limits.freeBytes === 'number' && totalBytes > limits.freeBytes) {
    problems.push(`${formatBytes(totalBytes)} does not fit: your sandbox has ${formatBytes(limits.freeBytes)} of disk free. Free some space (old projects, node_modules, build output) or ask your admin for a larger disk.`);
  }
  if (!problems.length && totalBytes > LARGE_UPLOAD_BYTES) {
    warnings.push(`This is a large upload (${formatBytes(totalBytes)}). It may take a while.`);
  }
  return {
    files,
    excluded,
    folderCount: folders.size,
    totalBytes,
    conflicts: files.filter((file) => file.exists).map((file) => file.destPath),
    problems,
    warnings,
  };
}

/** The most one batch carries (unpacked): a shorter request each, so a long upload shows its progress part by part. */
export const BATCH_BYTES = 150 * 1024 * 1024;

/** The batch size for the server's limits: BATCH_BYTES, or less when the server takes smaller archives. */
export function batchOptions(limits = SERVER_LIMITS) {
  return { maxBytes: Math.min(BATCH_BYTES, limits.maxArchiveBytes), maxFiles: limits.maxFiles };
}

/**
 * Splits the files into archives the server takes: each under `maxBytes` (counted unpacked, the worst case) and
 * `maxFiles`. Usually one: one archive, one commit.
 */
export function batchFiles(files, { maxBytes = BATCH_BYTES, maxFiles = SERVER_LIMITS.maxFiles } = {}) {
  const batches = [];
  let current = [];
  let bytes = 0;
  for (const file of files) {
    if (current.length && (bytes + file.size > maxBytes || current.length >= maxFiles)) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(file);
    bytes += file.size;
  }
  if (current.length) { batches.push(current); }
  return batches;
}

/** The new paths (and their folders) to unfold and select once uploaded. */
export function foldersToExpand(destPaths) {
  const folders = new Set();
  for (const destPath of destPaths) {
    const segments = cleanPath(destPath).split('/');
    for (let index = 1; index < segments.length; index += 1) { folders.add(segments.slice(0, index).join('/')); }
  }
  return [...folders];
}

/** Already-compressed formats are stored, not deflated again (faster, same size). */
const STORED_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'ico', 'zip', 'gz', 'tgz', 'bz2', 'xz', '7z', 'rar', 'jar', 'war', 'woff', 'woff2', 'mp3', 'mp4', 'm4a', 'mov', 'webm', 'ogg', 'pdf', 'docx', 'xlsx', 'pptx']);
export function compressionLevel(relPath) {
  const extension = String(relPath).split('.').pop().toLowerCase();
  return STORED_EXTENSIONS.has(extension) ? 0 : 6;
}

export function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) { return '0 B'; }
  if (bytes < 1024) { return `${bytes} B`; }
  if (bytes < 1048576) { return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`; }
  if (bytes < 1073741824) { return `${(bytes / 1048576).toFixed(1)} MB`; }
  return `${(bytes / 1073741824).toFixed(2)} GB`;
}

/**
 * What a running upload shows while it sends (runUpload's progress): "Sending part 2 of 7 · 300.0 MB of 1.00 GB", or
 * just the part when the sizes are unknown. '' before anything is sent.
 */
export function sendingDetail(progress) {
  if (!progress || !['sending', 'sent'].includes(progress.phase)) { return ''; }
  const part = progress.batches > 1 ? `part ${progress.batch + 1} of ${progress.batches}` : '';
  const bytes = progress.totalBytes > 0 ? `${formatBytes(progress.sentBytes || 0)} of ${formatBytes(progress.totalBytes)} sent` : '';
  return [part, bytes].filter(Boolean).join(' · ');
}

/**
 * How far a running upload is, 0–95 (100 is the answer): half for the files read and packed (`readFiles` of
 * `fileCount`), half for the bytes the server answered for, so a long send of large parts still moves part by part.
 */
export function uploadPercent(progress, readFiles, fileCount) {
  if (!fileCount) { return 0; }
  const packed = Math.min(1, readFiles / fileCount);
  const sent = progress?.totalBytes > 0 ? Math.min(1, (progress.sentBytes || 0) / progress.totalBytes) : packed;
  return Math.round(95 * ((packed + sent) / 2));
}

/** "Uploaded 23 files to services/app/src" style summary of a server answer's statuses. */
export function summarizeResults(results) {
  const counts = { created: 0, overwritten: 0, renamed: 0, skipped: 0, failed: 0, cancelled: 0 };
  for (const result of results) { counts[result.status] = (counts[result.status] || 0) + 1; }
  return counts;
}
